import {
  ACTIVATED_CRMS,
  BOTS,
  CLONE_WEBHOOK,
  CONNECTIONS,
  CRMS_LEADS_COUNT,
  TEST_WEBHOOK,
  WEBHOOK,
  WEBHOOKS,
  WEBHOOK_JSON_TEMPLATES,
  WIDGET_CONNECTIONS,
} from '@mm/api-routes';
import { snake } from '../helpers/converterString';
import ConnectionModel, {
  ActiveCRMSResponse,
  IntegrationTypes,
} from '../model/ConnectionModel';
import ConnectionPaginationModel from '../model/ConnectionPaginationModel';
import { ConnectionVariableModel } from '../model/ConnectionVariableModel';
import AuthenticatedController from './AuthenticatedController';
import Controller, { DEFAULT_HEADERS } from './Controller';

export default class ConnectionsController extends AuthenticatedController {
  // connection is not ConnectionModel but some type of mutated connection model with extra props
  // so not typing for now
  static async postWebhook(
    facebookPageId: number,
    connection,
  ): Promise<ConnectionModel> {
    const response = await super.post(WEBHOOKS(facebookPageId), connection);

    return response.webhook;
  }

  static async clone(
    facebookPageId: number,
    connectionId: number,
  ): Promise<ConnectionModel> {
    const response = await super.post(
      CLONE_WEBHOOK(facebookPageId, connectionId),
    );

    return response.webhook;
  }

  static async update(
    facebookPageId: number,
    connection: ConnectionModel,
  ): Promise<ConnectionModel> {
    const response = await super.put(
      `${WEBHOOKS(facebookPageId)}/${connection.id}`,
      connection,
    );

    return response.webhook;
  }

  static async getById(
    facebookPageId: number,
    connectionId: number,
  ): Promise<ConnectionModel> {
    const response = await super.get(WEBHOOK(facebookPageId, connectionId));

    return response;
  }

  static async getAll(
    facebookPageId: number,
    params?: {
      pageNumber?: number;
      pageSize?: number;
      query?: string;
      type?: IntegrationTypes;
      queryOptions?: {
        withVariables?: boolean;
        includedNames?: 'all' | string[] | null;
        withWebhookErrors?: boolean;
      };
    },
  ): Promise<ConnectionPaginationModel> {
    const response = await super.get(
      CONNECTIONS(
        facebookPageId,
        params?.pageNumber,
        params?.pageSize,
        params?.query,
        params?.type,
        params?.queryOptions,
      ),
    );

    return response;
  }

  static async getWidgetConnections(facebookPageId: number, type?: string) {
    const response = await super.get(WIDGET_CONNECTIONS(facebookPageId, type));

    return response;
  }

  static async deleteWebhook(facebookPageId: number, connectionId: number) {
    return super.delete(WEBHOOK(facebookPageId, connectionId));
  }

  // Prefered not to use the AuthenticatedController here because it mutates the keys of the
  // JSON response.
  static async testWebhook(
    facebookPageId: number,
    connectionId: number,
    variables: ConnectionVariableModel[],
    urlVariables: { name: string; value: string }[],
    remoteUserId?: string,
  ) {
    const updatedVariables = variables.map(item => ({
      name_sent_as: item.sentAs,
      value: item.value,
    }));
    const updatedUrlVariables = urlVariables.map(item => ({
      name_sent_as: item.name,
      value: item.value,
    }));

    const url = TEST_WEBHOOK(facebookPageId, connectionId);
    const body = remoteUserId
      ? {
          remoteUserId,
        }
      : {
          testVariables: updatedVariables,
          testUrlVariables: updatedUrlVariables,
        };

    const response = await fetch(url, {
      method: 'POST',
      headers: AuthenticatedController.addAuthTokenToHeaders(DEFAULT_HEADERS),
      body: JSON.stringify(snake(body)),
    });

    await Controller.handleFailedResponse(response);
    const responseJson = await response.json();

    return responseJson;
  }

  // connection is not ConnectionModel but some type of mutated connection model with extra props
  // so not typing for now
  static async putWebhook(connection, facebookPageId: number) {
    const {
      name,
      description,
      url,
      kind,
      variables: externalVariables,
      id,
      audienceId,
      active,
      requestType,
      responseType,
    } = connection;

    const variables = externalVariables.map(item => ({
      name: item.name,
      name_sent_as: item.sentAs,
      static: item.static,
      value: item.value,
    }));
    return super.put(WEBHOOK(facebookPageId, id), {
      name,
      description,
      url,
      kind,
      variables,
      audienceId,
      active,
      requestType,
      responseType,
    });
  }

  static async getVariableOptions(
    botId: number,
    variableName: number,
    variableType: string,
  ) {
    return super.get(
      `${BOTS}/${botId}/audiences/variable_options?variable_name=${variableName}&variable_type=${variableType}`,
    );
  }

  static async getActivatedCrms(
    facebookPageId: number,
  ): Promise<ActiveCRMSResponse> {
    return super.get(ACTIVATED_CRMS(facebookPageId));
  }

  static async getCrmsLeadsSent(
    facebookPageId: number,
  ): Promise<ActiveCRMSResponse> {
    return super.get(CRMS_LEADS_COUNT(facebookPageId));
  }

  static async getWebhookJsonTemplates(): Promise<any> {
    return super.get(WEBHOOK_JSON_TEMPLATES);
  }
}
